"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { UserMenu } from "./UserMenu";
import NotificationBell from "./NotificationBell";
import { useI18n } from "@/lib/i18n";

export function Navbar() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const { t } = useI18n();

  const modules = [
    { name: t("nav.dashboard"), href: "/" },
    { name: t("nav.production"), href: "/production" },
    { name: t("nav.shipping"), href: "/shipping" },
    { name: t("nav.inventory"), href: "/inventory" },
    { name: t("nav.admin"), href: "/admin" },
  ];

  return (
    <header className="sticky top-0 z-50 backdrop-blur bg-neutral-900/80 border-b border-neutral-800">
      <div className="flex items-center justify-between px-4 md:px-6 h-16">
        {/* Left: brand */}
        <div className="flex items-center gap-8 xl:gap-10">
          <Link
            href="/"
            aria-label="Nordlicht Manufaktur – Production Hub"
            className="flex items-center gap-3 shrink-0"
          >
            <img
              src="https://nordlicht-manufaktur.de/img/logo_light.png"
              alt="Nordlicht by Piatho"
              className="h-9 w-auto max-w-[9.5rem] object-contain"
            />
            <span className="hidden sm:inline border-l border-neutral-700 pl-3 text-xs font-medium uppercase tracking-[0.18em] text-gray-300">
              Production Hub
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1 text-sm">
            {modules.map((m) => (
              <Link
                key={m.href}
                href={m.href}
                className={`rounded-lg px-3 py-2 transition-colors ${
                  pathname === m.href
                    ? "bg-neutral-800 text-aurora-400 font-medium"
                    : "text-gray-300 hover:bg-neutral-800/70 hover:text-aurora-300"
                }`}
              >
                {m.name}
              </Link>
            ))}
          </nav>
        </div>

        {/* Right */}
        <div className="flex items-center gap-2">
          {/* ✅ Notifications */}
          <NotificationBell />

          {/* Mobile Hamburger */}
          <button
            onClick={() => setOpen(!open)}
            className="md:hidden inline-flex items-center justify-center rounded-lg p-2 text-gray-300 hover:bg-neutral-800"
            aria-label={t("nav.open")}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              {open ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>

          <UserMenu />
        </div>
      </div>

      {/* Mobile Menu */}
      {open && (
        <div className="md:hidden border-t border-neutral-800 bg-neutral-900/95">
          <nav className="flex flex-col px-4 py-3 space-y-1 text-sm">
            {modules.map((m) => (
              <Link
                key={m.href}
                href={m.href}
                onClick={() => setOpen(false)}
                className={`rounded-lg px-3 py-2 transition ${
                  pathname === m.href
                    ? "bg-neutral-800 text-aurora-400 font-medium"
                    : "text-gray-300 hover:bg-neutral-800 hover:text-aurora-300"
                }`}
              >
                {m.name}
              </Link>
            ))}

            {/* ✅ Shortcut im Mobile Menü */}
            <Link
              href="/settings/notifications"
              onClick={() => setOpen(false)}
              className="rounded-lg px-3 py-2 transition text-gray-300 hover:bg-neutral-800 hover:text-aurora-300"
            >
              {t("nav.notifications")}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
